import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardBody } from 'reactstrap';
import BaseSpinWrapper from 'components/atoms/base-spin-wrapper';
import MonthPicker from 'components/molecules/datepicker/monthPicker';
import { useGetPatientEngagementSuccessRate } from 'infrastructure/hooks/patient-engagement-report';

import s from './styles.module.scss';

import type { TPatientEngagementReportFilter } from 'infrastructure/hooks/patient-engagement-report';

interface IProps {
  filter: TPatientEngagementReportFilter;
  updateFilter: (newFilter: Partial<TPatientEngagementReportFilter>) => void;
}

const AttemptSuccessTrackingTiles: React.FC<IProps> = ({
  filter,
  updateFilter,
}) => {
  const { t } = useTranslation();
  const { tiles, isLoading } = useGetPatientEngagementSuccessRate(filter);

  const rows = [
    {
      key: 'callingQueueToOnboarding',
      label: t('patientEngagementReport.callingQueueToOnboarding'),
      theme: s.purpleTheme,
      counts: tiles.callingQueueToOnboarding,
    },
    {
      key: 'onboardingTwoWaySync',
      label: t('patientEngagementReport.onboardingToActiveTwoWaySync'),
      theme: s.greenTheme,
      counts: tiles.onboardingTwoWaySync,
    },
  ] as const;

  const bucketLabels: Array<{
    key: 'first' | 'second' | 'thirdPlus';
    label: string;
  }> = [
    {
      key: 'first',
      label: t('patientEngagementReport.firstCallAttemptSuccess'),
    },
    {
      key: 'second',
      label: t('patientEngagementReport.secondCallAttemptSuccess'),
    },
    {
      key: 'thirdPlus',
      label: t('patientEngagementReport.thirdPlusCallAttemptSuccess'),
    },
  ];

  return (
    <div className={s.wrapper}>
      <div className={s.header}>
        <h4 className={s.title}>
          {t('patientEngagementReport.attemptSuccessTracking')}
        </h4>
        <div className={s.headerControls}>
          <span className={s.note}>
            {t('patientEngagementReport.resultsReflectAppliedFilters')}
          </span>
          <MonthPicker
            value={{ year: filter.year, month: filter.month }}
            maxDate={new Date()}
            onChange={(value) => {
              if (!value) return;
              updateFilter({ year: value.year, month: value.month });
            }}
          />
        </div>
      </div>
      <BaseSpinWrapper spinning={isLoading}>
        {rows.map((row) => (
          <div key={row.key} className={s.row}>
            <div className={s.rowLabel}>{row.label}</div>
            <div className={s.tiles}>
              {bucketLabels.map((bucket) => (
                <Card key={bucket.key} className={`${s.tile} ${row.theme}`}>
                  <CardBody className={s.tileBody}>
                    <div className={s.tileValue}>{row.counts[bucket.key]}</div>
                    <div className={s.tileLabel}>{bucket.label}</div>
                  </CardBody>
                </Card>
              ))}
            </div>
          </div>
        ))}
      </BaseSpinWrapper>
    </div>
  );
};

export default AttemptSuccessTrackingTiles;
